import * as React from 'react';
import { useBoolean } from '@sravni/react-utils';
import { getCoreRowModel } from '@tanstack/react-table';
import { Space } from '../../Space/src/Space.js';
import { useTable } from '../hooks/useTable.js';
import { getDefaultColumns } from '../utils/getDefaultColumns.js';
import { globalFilterFn } from '../utils/globalFilterFn.js';
import { ExpandColumn } from './ExpandColumn.js';
import { GlobalSearch } from './GlobalSearch.js';
import { TableContent } from './TableContent.js';

/**
 * @description Table component.
 *
 * @component
 * @example
 * ```jsx
 * <Table>content</Table>
 * ```
 */
const InnerTable = (props, ref) => {
    const { options, className, topRightAddon, renderSubComponent, ...restProps } = props;
    const { manualPagination, data, columns, enableGlobalFilter = false, state, getRowCanExpand, onPaginationChange, isLoading = false, size = 16, ...restOptions } = options;
    const { pagination, ...restState } = state ?? {};
    const [isSearchFocused, setIsSearchFocused] = useBoolean(false);
    const [globalFilter, setGlobalFilter] = React.useState('');
    const [innerPagination, setInnerPagination] = React.useState({
        pageIndex: state?.pagination?.pageIndex ?? 0,
        pageSize: state?.pagination?.pageSize ?? 10,
    });
    const handleGlobalSearch = React.useCallback((event) => setGlobalFilter(String(event.currentTarget.value)), []);
    const handlePaginationChange = React.useCallback((updater) => {
        if (typeof updater !== 'function') {
            return;
        }
        const nextState = updater(innerPagination);
        setInnerPagination(updater);
        onPaginationChange?.(nextState?.pageIndex);
    }, [innerPagination, onPaginationChange]);
    const defaultColumns = getDefaultColumns(data);
    const innerColumns = columns ?? defaultColumns;
    if (getRowCanExpand && ExpandColumn) {
        innerColumns.unshift(ExpandColumn);
    }
    const table = useTable({
        data,
        columns: innerColumns,
        enableGlobalFilter,
        manualPagination,
        getRowCanExpand,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: handlePaginationChange,
        state: {
            globalFilter,
            pagination: innerPagination,
            ...restState,
        },
        ...restOptions,
    });
    return (React.createElement("div", { ref: ref, className: className, "data-qa": "Table", ...restProps },
        (enableGlobalFilter || topRightAddon) && (React.createElement(Space, { justify: "space-between", size: 8, className: "h-mb-24" },
            enableGlobalFilter && (React.createElement(GlobalSearch, { globalFilter: globalFilter, isSearchFocused: isSearchFocused, setIsSearchFocused: setIsSearchFocused, handleGlobalSearch: handleGlobalSearch })),
            topRightAddon)),
        React.createElement(TableContent, { table: table, manualPagination: manualPagination, isLoading: isLoading, globalFilter: globalFilter, renderSubComponent: renderSubComponent, size: size })));
};
InnerTable.displayName = 'Table';
const Table = React.forwardRef(InnerTable);

export { Table };
//# sourceMappingURL=Table.js.map
